/* eslint-disable better-mutation/no-mutation */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import config from 'tradera/apps/syi/services/config';
import { ITEM_TYPE } from 'tradera/apps/syi/state/constants';

type ValidationErrors = {
    fixedPrice?: string | null;
    openingBid?: string | null;
    auctionBinPrice?: string | null;
    reservePrice?: string | null;
};

type PriceState = {
    openingBid: number | null;
    auctionBinPrice: number | null;
    fixedPrice: number | null;
    reservePrice: number | null;
    showReservePrice: boolean;
    showCharityDonation: boolean;
    editCharity: boolean;
    isAuctionBinSelected: boolean | null;
    isPureBinSelected: boolean | null;
    validationErrors: ValidationErrors;
    selectableVats: number[];
    reservePriceCanChangeButNotBeRemoved: boolean;
    vat: number | null;
    reservePriceCost: number;
    isDirty: boolean;
    selectedItemType: (typeof ITEM_TYPE)[keyof typeof ITEM_TYPE];
};

function isValidPrice(number: number | null): boolean {
    if (number === null || number < 1) return false;
    const str = String(number);
    if (!Number.isFinite(parseInt(str))) {
        return false;
    } else if (str.indexOf('.') > -1) {
        return false;
    } else if (str.indexOf(',') > -1) {
        return false;
    }
    return true;
}

function isInt(number: number | null): number is number {
    if (number === null) return false;
    return number % 1 === 0;
}

function validateOpeningBid({
    isPureBinSelected,
    openingBid
}: Pick<PriceState, 'isPureBinSelected' | 'openingBid'>): string | null {
    if (isPureBinSelected) {
        return null;
    }

    if (openingBid && !isInt(openingBid)) {
        return 'syi_validation_amount_must_be_whole_number';
    }

    const maxPrice = 10000000;
    const minPrice = 1;

    if (!isValidPrice(openingBid)) {
        return 'syi_validation_opening_bid_missing';
    }

    if (openingBid && openingBid > maxPrice) {
        return 'syi_validation_max_opening_bid';
    }

    if (openingBid && openingBid < minPrice) {
        return 'syi_validation_minimum_opening_bid';
    }

    return null;
}

function validateReservePrice({
    isPureBinSelected,
    showReservePrice,
    openingBid,
    reservePrice,
    auctionBinPrice
}: Pick<
    PriceState,
    | 'isPureBinSelected'
    | 'showReservePrice'
    | 'openingBid'
    | 'reservePrice'
    | 'auctionBinPrice'
>): string | null {
    if (isPureBinSelected) {
        return null;
    }

    if (showReservePrice && !reservePrice) {
        return 'syi_validation_amount_missing';
    }

    if (reservePrice && !isInt(reservePrice)) {
        return 'syi_validation_amount_must_be_whole_number';
    }

    const maxPrice = 10000000;

    if (reservePrice && !isValidPrice(reservePrice)) {
        return 'syi_validation_invalid_reservation_price';
    }

    const reservePriceIsLessThanOpeningBid =
        reservePrice && reservePrice <= (openingBid ?? 0);
    if (reservePriceIsLessThanOpeningBid) {
        return 'syi_validation_reservation_must_be_larger_than_starting';
    }

    const reservePriceIsGreaterThanAuctionBinPrice =
        reservePrice && auctionBinPrice && reservePrice >= auctionBinPrice;
    if (reservePriceIsGreaterThanAuctionBinPrice) {
        return 'syi_validation_reservation_must_be_less_than_buy_now';
    }

    if (reservePrice && reservePrice > maxPrice) {
        return 'syi_validation_max_reservation_price';
    }

    return null;
}

function validateAuctionBinPrice({
    isAuctionBinSelected,
    isPureBinSelected,
    openingBid,
    reservePrice,
    auctionBinPrice
}: Pick<
    PriceState,
    | 'isAuctionBinSelected'
    | 'isPureBinSelected'
    | 'openingBid'
    | 'reservePrice'
    | 'auctionBinPrice'
>): string | null {
    if (isPureBinSelected) {
        return null;
    }

    if (isAuctionBinSelected && !isInt(auctionBinPrice)) {
        return 'syi_validation_amount_missing';
    }

    const auctionBinPriceIsLessThanOpeningBid =
        auctionBinPrice && auctionBinPrice <= (openingBid ?? 0);
    if (auctionBinPriceIsLessThanOpeningBid) {
        return 'syi_validation_buy_now_must_be_larger_than_starting';
    }

    const reservePriceIsGreaterThanAuctionBinPrice =
        reservePrice && auctionBinPrice && reservePrice >= auctionBinPrice;
    if (reservePriceIsGreaterThanAuctionBinPrice) {
        return 'syi_validation_buy_now_must_be_larger_than_reservation';
    }

    return null;
}

function validateFixedPrice({
    isPureBinSelected,
    fixedPrice
}: Pick<PriceState, 'isPureBinSelected' | 'fixedPrice'>): string | null {
    if (!isPureBinSelected) {
        return null;
    }

    if (fixedPrice && !isInt(fixedPrice)) {
        return 'syi_validation_amount_must_be_whole_number';
    }

    const maxPrice = 10000000;
    const minPrice = 1;

    if (!isValidPrice(fixedPrice)) {
        return 'syi_validation_enter_fixed_price';
    }

    if (!isNaN(Number(fixedPrice))) {
        if (fixedPrice && fixedPrice > maxPrice) {
            return 'syi_validation_fixed_price_max_value';
        }

        if (fixedPrice && fixedPrice < minPrice) {
            return 'syi_validation_fixed_price_min_value';
        }
    }

    return null;
}

function validate(newState: PriceState): PriceState {
    const validationErrors: ValidationErrors = {};

    validationErrors.fixedPrice = validateFixedPrice(newState);
    validationErrors.openingBid = validateOpeningBid(newState);
    validationErrors.auctionBinPrice = validateAuctionBinPrice(newState);
    validationErrors.reservePrice = validateReservePrice(newState);

    // Remove null validation errors
    Object.keys(validationErrors).forEach((key) => {
        if (!validationErrors[key as keyof ValidationErrors]) {
            delete validationErrors[key as keyof ValidationErrors];
        }
    });

    const isDirty =
        newState.openingBid !== initialState.openingBid ||
        newState.auctionBinPrice !== initialState.auctionBinPrice ||
        newState.fixedPrice !== initialState.fixedPrice ||
        newState.reservePrice !== initialState.reservePrice;

    return { ...newState, validationErrors, isDirty };
}

const queryParams = new URLSearchParams(
    typeof window !== 'undefined' ? window?.location?.search : ''
);

function toIntOrNull(
    numberOrString: string | number | null | undefined
): number | null {
    if (numberOrString === null || numberOrString === undefined) {
        return null;
    }

    const number =
        typeof numberOrString === 'number'
            ? numberOrString
            : parseInt(numberOrString);

    if (isFinite(number)) {
        return number;
    }
    return null;
}

const initialState: PriceState = {
    openingBid: null,
    auctionBinPrice: null,
    fixedPrice: null,
    reservePrice: null,
    showReservePrice: false,
    showCharityDonation: Boolean(queryParams.get('charityId')),
    editCharity: false,
    isAuctionBinSelected: null,
    isPureBinSelected: null,
    validationErrors: {},
    selectableVats: config.selectableVatValues,
    reservePriceCanChangeButNotBeRemoved: false,
    vat: null,
    reservePriceCost: 40,
    isDirty: false,
    selectedItemType: ITEM_TYPE.AUCTION
};

const priceSlice = createSlice({
    name: 'price',
    initialState,
    reducers: {
        setOpeningBid: (state, action: PayloadAction<number | null>) => {
            state.openingBid = toIntOrNull(action.payload);
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setAuctionBinPrice: (
            state,
            action: PayloadAction<number | null | undefined>
        ) => {
            state.auctionBinPrice = toIntOrNull(action.payload);
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setFixedPrice: (
            state,
            action: PayloadAction<number | null | undefined>
        ) => {
            state.fixedPrice = toIntOrNull(action.payload);
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setReservePrice: (state, action: PayloadAction<number | null>) => {
            state.reservePrice = toIntOrNull(action.payload);
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setReservePriceVisibility: (state, action: PayloadAction<boolean>) => {
            state.showReservePrice = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setCharityDonationVisibility: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.showCharityDonation = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setEditCharity: (state, action: PayloadAction<boolean>) => {
            state.editCharity = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setIsPureBinSelected: (
            state,
            action: PayloadAction<boolean | null>
        ) => {
            state.isPureBinSelected = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setIsAuctionBinSelected: (
            state,
            action: PayloadAction<boolean | null>
        ) => {
            state.isAuctionBinSelected = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setVat: (state, action: PayloadAction<number | null>) => {
            state.vat = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setSelectedItemType: (
            state,
            action: PayloadAction<(typeof ITEM_TYPE)[keyof typeof ITEM_TYPE]>
        ) => {
            state.selectedItemType = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        resetPricesAndVat: (state) => {
            state.openingBid = null;
            state.auctionBinPrice = null;
            state.fixedPrice = null;
            state.vat = null;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        },
        setReservePriceCanChangeButNotBeRemoved: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.reservePriceCanChangeButNotBeRemoved = action.payload;
            const validated = validate(state);
            state.validationErrors = validated.validationErrors;
            state.isDirty = validated.isDirty;
        }
    }
});

export const {
    setOpeningBid,
    setAuctionBinPrice,
    setFixedPrice,
    setReservePrice,
    setReservePriceVisibility,
    setCharityDonationVisibility,
    setEditCharity,
    setIsAuctionBinSelected,
    setIsPureBinSelected,
    setVat,
    setSelectedItemType,
    resetPricesAndVat,
    setReservePriceCanChangeButNotBeRemoved
} = priceSlice.actions;

export default priceSlice.reducer;
